import React from 'react';
import { Loader2 } from 'lucide-react';
import BrandBriefCard from './BrandBriefCard';
import ArtDirectionCard from './ArtDirectionCard';
import PremiumLiveActivity from './PremiumLiveActivity';
import Button from '../../../../components/Button';
import type { BrandTeamSession, BrandTeamAgentLog } from '../../../../services/brandTeamService';

interface StepBriefProps {
  session: BrandTeamSession;
  agentLogs: BrandTeamAgentLog[];
  onApprove: () => void;
  approving: boolean;
  onNext: () => void;
}

const StepBrief: React.FC<StepBriefProps> = ({ session, agentLogs, onApprove, approving, onNext }) => {
  const brief = session.brandBrief || {};
  const hasBrief = Object.keys(brief).length > 0;
  const isApproved = !!session.briefApprovedAt;
  const artDirection = session.metadata?.artDirection as Record<string, any> | undefined;
  const isArtDirectionGenerating = hasBrief && !artDirection && !isApproved;

  // Loading state - brief not yet generated
  if (!hasBrief) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-brand-purple" />
        <h3 className="text-lg font-bold text-text-dark">Your Brand Manager is preparing the strategic brief...</h3>
        <p className="mt-2 text-sm text-body-grey">This usually takes 15-30 seconds</p>
        {agentLogs.length > 0 && (
          <div className="mt-8 mx-auto max-w-lg">
            <PremiumLiveActivity logs={agentLogs} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-text-dark">Review Strategic Brief</h2>
        <p className="mt-1 text-sm text-body-grey">Review your brand brief and art direction before proceeding.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <BrandBriefCard
            brief={brief}
            status={session.status}
            briefApprovedAt={session.briefApprovedAt}
            onApprove={onApprove}
            loading={approving}
          />
        </div>
        <div className="lg:col-span-2">
          <ArtDirectionCard
            artDirection={artDirection}
            isGenerating={isArtDirectionGenerating}
          />
        </div>
      </div>

      {/* Bottom action */}
      {isApproved && (
        <div className="mt-8 flex justify-end">
          <Button variant="primary" size="large" onClick={onNext}>
            Continue to Plan →
          </Button>
        </div>
      )}
    </div>
  );
};

export default StepBrief;
